<template>
  <div class="container mt-4" dir="rtl">
    
    <!-- 1. الفلاتر والبحث -->
    <div class="card shadow-sm mb-4 border-top-warning">
      <div class="card-header bg-white py-3 d-flex justify-content-between align-items-center">
        <h5 class="mb-0 text-dark fw-bold">
          <i class="fas fa-edit ms-2 text-warning"></i> تصويب وتعديل رصد (فردي)
        </h5>
        <span class="badge bg-warning text-dark">تعديل دقيق</span>
      </div>
      
      <div class="card-body">
        <form @submit.prevent="searchStudent">
            
            <!-- الصف الأول: الفلاتر الأساسية -->
            <div class="row g-3 mb-3">
                <div class="col-md-4">
                    <label class="form-label fw-bold small">الفصل الدراسي</label>
                    <select v-model="filters.term" @change="getSubjectswithtearm" class="form-select">
                        <option value="">-- اختر الفصل --</option>
                        <option v-for="t in terms" :key="t.id" :value="t">{{ t.name }}</option>
                    </select>
                </div>
                <div class="col-md-4">
                    <label class="form-label fw-bold small">المادة</label>
                    <select v-model="filters.subject" class="form-select">
                        <option value="">-- اختر المادة --</option>
                        <option v-for="s in subjects" :key="s.id" :value="s">{{ s.name }}</option>
                    </select>
                </div>
                <div class="col-md-4">
                    <label class="form-label fw-bold small">المجموعة (اختياري)</label>
                    <select v-model="filters.group" class="form-select">
                        <option value="">-- الكل --</option>
                        <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.group_name }}</option>
                    </select>
                </div>
            </div>

            <!-- الصف الثاني: حقل البحث وزر التنفيذ -->
            <div class="row g-3 align-items-end">
                <div class="col-md-6">
                    <label class="form-label fw-bold text-primary">البحث بالرقم السري (آخر 3 أرقام يكفي)</label>
                    <div class="input-group input-group-lg">
                        <span class="input-group-text bg-warning border-warning text-dark fw-bold">
                            <i class="fas fa-key"></i>
                        </span>
                        <input 
                        ref="searchInput"
                            v-model="searchQuery" 
                            type="number" 
                            class="form-control fw-bold fs-4 text-center" 
                            placeholder="... 123" 
                            required
                            :disabled="!filters.term || !filters.subject"
                        >
                        <button type="submit" class="btn btn-dark px-4 fw-bold" :disabled="loading || !filters.term || !filters.subject">
                            <span v-if="loading" class="spinner-border spinner-border-sm"></span>
                            <span v-else>بحث وتعديل</span>
                        </button>
                    </div>
                </div>
                
                <div class="col-md-6">
                    <div class="alert alert-info py-2 mb-0 small">
                        <i class="fas fa-info-circle ms-1"></i> 
                        اختر الفصل والمادة أولاً، ثم اكتب الرقم السري للطالب لتعديل درجته في هذه المادة تحديداً.
                    </div>
                </div>
            </div>

        </form>
      </div>
    </div>

    <!-- 2. منطقة التعديل (تظهر بعد البحث) -->
    <div v-if="student" class="card shadow-lg border-0 animate__animated animate__fadeIn">
      
      <!-- ترويسة الطالب -->
      <div class="card-header bg-dark text-white p-3">
         <div class="row align-items-center">
            <div class="col-md-8">
                <h4 class="mb-1 text-warning">{{ student.name }}</h4>
                <div class="d-flex gap-3 text-white-50 small mt-2">
                    <span><i class="fas fa-school ms-1"></i> {{ student.school_name }}</span>
                    <span class="vr"></span>
                    <span><i class="fas fa-id-card ms-1"></i> جلوس: {{ student.seat_no }}</span>
                    <span class="vr"></span>
                    <span class="text-white fw-bold"><i class="fas fa-key ms-1 text-warning"></i> سري: {{ student.secret_no }}</span>
                </div>
            </div>
            <div class="col-md-4 text-end">
                <div class="badge bg-primary fs-6">
                    {{ filters.subject.name }}
                </div>
            </div>
         </div>
      </div>

      <!-- جسم التعديل -->
      <div class="card-body p-5">
        <div class="row justify-content-center">
            <div class="col-md-8">
                
                <!-- عرض الدرجة الحالية والجديدة -->
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <label class="form-label fw-bold text-muted">الدرجة الحالية: <span class="text-danger fs-5">{{ currentGradeText }}</span></label>
                    <label class="form-label fw-bold text-primary">الدرجة الجديدة:</label>
                </div>

                <div class="input-group input-group-lg mb-3">
                    <!-- حقل الكتابة -->
                    <input 
                        type="number" 
                        v-model.number="newGrade" 
                        class="form-control text-center fw-bold fs-2 text-primary border-primary"
                        :class="{'is-invalid': hasError}"
                        placeholder="00"
                        ref="gradeInput"
                        @keyup.enter="saveChanges"
                    >
                    
                    <!-- أزرار الحالات الخاصة -->
                    <button @click="newGrade = -1" class="btn btn-outline-secondary px-3 fw-bold" title="غياب">غ</button>
                    <button @click="newGrade = -2" class="btn btn-outline-info px-3 fw-bold" title="لغات">لغـ</button>
                    <button @click="newGrade = -3" class="btn btn-outline-warning text-dark px-3 fw-bold" title="دمج">دمج</button>
                </div>

                <!-- رسائل الخطأ -->
                <div v-if="hasError" class="text-danger fw-bold mb-3 text-center animate__animated animate__headShake">
                    <i class="fas fa-exclamation-triangle ms-1"></i> الدرجة تتجاوز النهاية العظمى ({{ filters.subject.max }})
                </div>

                <!-- زر الحفظ -->
                <button @click="saveChanges" class="btn btn-success btn-lg w-100 fw-bold shadow mt-2" :disabled="isSaving || hasError || newGrade === ''">
                    <span v-if="isSaving" class="spinner-border spinner-border-sm ms-2"></span>
                    <i class="fas fa-save ms-2"></i> حفظ التعديل
                </button>

            </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import api from '@/api/axios';
import Swal from 'sweetalert2';

export default {
  name: "SingleGradeEdit",
  data() {
    return {
      searchQuery: '',
      loading: false,
      isSaving: false,
      
      // البيانات الأساسية
      terms: [],
      subjects: [],
      groups: [],
      
      // الفلاتر المختارة
      filters: {
          term: "",
          subject: "",
          group: ""
      },

      student: null,
      newGrade: '',
      originalGrade: ''
    };
  },
  computed: {
    hasError() {
      if(!this.filters.subject || this.newGrade === '') return false;
      if(this.newGrade < 0) return false; // الحالات الخاصة مقبولة
      return this.newGrade > this.filters.subject.max;
    },
    currentGradeText() {
        if(this.originalGrade === null) return 'لا يوجد';
        if(this.originalGrade == -1) return 'غائب';
        if(this.originalGrade == -2) return 'لغات';
        if(this.originalGrade == -3) return 'دمج';
        return this.originalGrade;
    }
  },
  mounted() {
      this.fetchInitialData();
  },
  methods: {
    async fetchInitialData() {
      try {
        const [termsRes, groupsRes] = await Promise.all([
          api.get("/getterms"),
          api.get("/getgroups")
        ]);
        this.terms = termsRes.data;
        this.groups = groupsRes.data;
      } catch (e) { console.error(e); }
    },

    async getSubjectswithtearm() {
      if (!this.filters.term) return;
      try {
        const res = await api.get("/getSubject/" + this.filters.term.id);
        this.subjects = res.data;
      } catch (e) { console.error(e); }
    },

    async searchStudent() {
      if(!this.searchQuery || !this.filters.subject) return;
      
      this.loading = true;
      this.student = null;

      try {
        // إرسال الفلاتر + الرقم للباك إند
        const res = await api.post('/control/search-student-advanced', {
            query: this.searchQuery, // الرقم المدخل (3 أرقام أو كامل)
            term_id: this.filters.term.id,
            subject_id: this.filters.subject.id,
            group_id: this.filters.group // اختياري
        });

        this.student = res.data.student;
        this.originalGrade = res.data.grade; // الدرجة الحالية في هذه المادة
        this.newGrade = res.data.grade; // نضعها افتراضياً للتعديل
        
        // التركيز على حقل الدرجة فوراً
       setTimeout(() => {
        if(this.$refs.gradeInput) {
            this.$refs.gradeInput.focus();
            this.$refs.gradeInput.select();
        }
    }, 400); 

      } catch (e) {
        if(e.response && e.response.status === 404) {
            Swal.fire('غير موجود', 'لم يتم العثور على طالب بهذا الرقم السري في النطاق المحدد', 'warning');
        } else {
            console.error(e);
        }
      } finally {
        this.loading = false;
      }
    },

    async saveChanges() {
        if(this.hasError) return;
        
        this.isSaving = true;
        try {
            await api.post('/control/update-single-grade', {
                student_id: this.student.id,
                subject_code: this.filters.subject.code, // اسم العمود
                new_grade: this.newGrade
            });

            // تحديث محلي وتنبيه
            this.originalGrade = this.newGrade;
            
            await Swal.fire({
                icon: 'success',
                title: 'تم التعديل',
                showConfirmButton: false,
                timer: 1000,
                position: 'top-end',
                toast: true
            });
            
            // تصفير البحث للاستعداد للطالب التالي
            this.searchQuery = '';
            this.student = null;
 this.$nextTick(() => {
            if (this.$refs.searchInput) {
                this.$refs.searchInput.focus();
            }
        });
        } catch (e) {
            Swal.fire('خطأ', 'فشل الحفظ', 'error');
        } finally {
            this.isSaving = false;
        }
    }
  }
};
</script>

<style scoped>
.border-top-warning { border-top: 4px solid #ffc107; }
</style>